import React, { Fragment, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  Button,
  Dropdown,
  Input,
  InputOnChangeData,
  Pagination,
  Table,
} from 'semantic-ui-react';
import deburr from 'lodash/deburr';
import { useTable } from 'hooks/useTable';
import { pageSizeOptions } from 'hooks/useTablePaginating';
import { ApplicationSegment } from 'components/ApplicationSegment';
import { Link, useHistory } from 'react-router-dom';
import { EmptyStateMessage } from 'components/EmptyStateMessage';
import { useResponsive } from 'hooks/useResponsive';
import { ActiveIndicator } from 'components/ActiveIndicator';
import { InterventionSummary } from 'modules/intervention';
import { useInterventionTypes } from 'modules/intervention/hooks/useInterventionTypes';
import { ApplicationLoader } from 'components/ApplicationLoader';
import { ShowInactiveToggle } from 'components/ShowInactiveToggle';

const m = defineMessages({
  recordperpage: {
    id: 'Interventions.Table.RecordPerPage',
    defaultMessage: 'Interventions par page',
  },
  totalCount: {
    id: 'Interventions.Table.TotalCount',
    defaultMessage: 'Nombre total',
  },
  search: {
    id: 'Interventions.Table.Search',
    defaultMessage: 'Rechercher',
  },
  noRow: {
    id: 'Interventions.Table.NoRow',
    defaultMessage: "Il n'y a aucune intervention.",
  },
  noMatchFound: {
    id: 'Interventions.Table.NoMatchFound',
    defaultMessage: 'Aucune intervention ne correspond à votre recherche.',
  },
  name: {
    id: 'Interventions.Table.Name',
    defaultMessage: 'Nom',
  },
  type: {
    id: 'Interventions.Table.Type',
    defaultMessage: 'Type',
  },
  active: {
    id: 'Interventions.Table.Active',
    defaultMessage: 'Actif',
  },
  new: {
    id: 'Interventions.Table.New',
    defaultMessage: 'Créer une intervention',
  },
});

interface InterventionRow {
  id: number;
  name: string;
  type: string;
  isActive: boolean;
}

interface Props {
  interventions: InterventionSummary[];
}

export const InterventionsTable: React.FC<Props> = ({ interventions }) => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const { isMobile } = useResponsive();
  const { interventionTypeNamesByValue } = useInterventionTypes();
  const {
    currentPageRows,
    setRows,
    hasRows,
    matchFound,
    hasMultiplePages,
    pageCount,
    sortingColumn,
    sortingDirection,
    searchInRows,
    changeSortingColumn,
    currentPage,
    handlePageChange,
    filteredRows,
    pageSize,
    handlePageSizeChange,
    areRowsReady,
    showInactive,
    setShowInactive,
  } = useTable<InterventionRow>({
    sortingColumn: 'name',
    sortingDirection: 'ascending',
  });

  const isLoaded = areRowsReady;

  useEffect(() => {
    const rows: InterventionRow[] = interventions.map((u) => ({
      id: u.id,
      name: u.name,
      type: interventionTypeNamesByValue[u.interventionType],
      isActive: u.isActive,
    }));
    setRows(rows);
  }, [interventions, setRows, interventionTypeNamesByValue]);

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData
  ) => {
    searchInRows(data.value, (entryRow) =>
      deburr(
        Object.values([entryRow.id, entryRow.name, entryRow.type]).toString()
      )
    );
  };

  const handleRowClick = (id: number) => {
    history.push(`/admin/interventions/${id}`);
  };

  if (!isLoaded) {
    return <ApplicationLoader />;
  }

  const totalCountAndRecordPerPage = matchFound && (
    <Fragment>
      <div>
        {formatMessage(m.recordperpage)}:{' '}
        <Dropdown
          inline
          options={pageSizeOptions}
          value={pageSize}
          onChange={handlePageSizeChange}
        />
      </div>
      <div>
        {formatMessage(m.totalCount)}: {filteredRows.length}
      </div>
    </Fragment>
  );

  const searchInput = (
    <Input
      icon="search"
      placeholder={formatMessage(m.search)}
      onChange={handleSearchChange}
    />
  );

  const topBar = hasRows && (
    <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
      <div className="tw-flex-1">{totalCountAndRecordPerPage}</div>
      <div className="tw-flex tw-items-center">
        <ShowInactiveToggle
          showInactive={showInactive}
          onChange={setShowInactive}
        />
        {searchInput}
      </div>
    </div>
  );

  const table = (
    <Fragment>
      <Table
        sortable
        celled
        striped
        selectable
        size="small"
        className="text-sm"
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell
              sorted={sortingColumn === 'name' ? sortingDirection : undefined}
              onClick={changeSortingColumn('name')}
            >
              {formatMessage(m.name)}
            </Table.HeaderCell>
            <Table.HeaderCell
              sorted={sortingColumn === 'type' ? sortingDirection : undefined}
              onClick={changeSortingColumn('type')}
            >
              {formatMessage(m.type)}
            </Table.HeaderCell>

            {showInactive && (
              <Table.HeaderCell
                sorted={
                  sortingColumn === 'isActive' ? sortingDirection : undefined
                }
                onClick={changeSortingColumn('isActive')}
              >
                {formatMessage(m.active)}
              </Table.HeaderCell>
            )}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {currentPageRows.map((row) => (
            <Table.Row
              key={row.id}
              onClick={() => handleRowClick(row.id)}
              className="tw-cursor-pointer"
            >
              <Table.Cell collapsing>{row.name}</Table.Cell>
              <Table.Cell>{row.type}</Table.Cell>
              
              {showInactive && (
                <Table.Cell collapsing>
                  <div className="tw-flex tw-justify-center">
                    <ActiveIndicator active={row.isActive} />
                  </div>
                </Table.Cell>
              )}
            </Table.Row>
          ))}
          {!hasRows && (
            <Table.Row>
              <Table.Cell colSpan="3">
                <EmptyStateMessage message={formatMessage(m.noRow)} />
              </Table.Cell>
            </Table.Row>
          )}

          {hasRows && !matchFound && (
            <Table.Row>
              <Table.Cell colSpan="3">
                <EmptyStateMessage message={formatMessage(m.noMatchFound)} />
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </Fragment>
  );

  const pagination = hasMultiplePages && (
    <Pagination
      totalPages={pageCount}
      activePage={currentPage}
      onPageChange={handlePageChange}
    />
  );

  const bottomBarCenteringClass = isMobile ? 'tw-justify-center' : undefined;

  const bottomBar = (
    <div className={`tw-flex tw-items-baseline ${bottomBarCenteringClass}`}>
      {pagination}
      <div className="tw-flex-1" />
      <Button as={Link} to="/admin/interventions/new" primary>
        {formatMessage(m.new)}
      </Button>
    </div>
  );

  return (
    <Fragment>
      <ApplicationSegment>
        {topBar}
        {table}
        {bottomBar}
      </ApplicationSegment>
    </Fragment>
  );
};
